'use strict'

import React, { useState, useEffect, useRef } from 'react';
import {
    Button,
    Flex,
    Text,
    Box,
    Icon
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import { Image } from '@chakra-ui/react';
import {useBrand} from '../../../contexts/brand-context'

import { CustomClose } from '../../icons';

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

// Mock function to simulate the upload process
const mockUploadPhoto = async (photo) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve({
                submitPhotoUrl: 'https://c2.ugc.bazaarvoice.com/bvstaging/submit/14126/ps_photo_preview_a8c4qhp5eky6j0hli7oa4bbjp/photo.jpg',
                thumbnailUrl: 'https://c2.ugc.bazaarvoice.com/bvstaging/submit/14126/ps_photo_preview_a8c4qhp5eky6j0hli7oa4bbjp/photoThumb.jpg',
            });
        }, 1000); // Simulate network delay
    });
};

const ImageUploader = ({ reviewFormData, form }) => {
    const config = getConfig()
    const {brandId} = useBrand()
    const {site} = useMultiSite()
    const siteId = site?.id
    const BAZAARVOICE_MAX_PHOTOS = getSiteEnvVarValue(config.app, 'BAZAARVOICE_MAX_PHOTOS', siteId)
    const maxPhotos = BAZAARVOICE_MAX_PHOTOS ? Number(BAZAARVOICE_MAX_PHOTOS) : 6
    const [photoData, setPhotoData] = useState([]);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelectAndUpload = async (event) => {
        const selectedPhoto = event.target.files[0];

        if (!selectedPhoto) {
            setError('Please select a photo to upload.')
            return
        }

        const maxSizeInBytes = 10 * 1024 * 1024; // 10MB in bytes
        if (selectedPhoto.size > maxSizeInBytes) {
            setError('File size exceeds 10MB. Please select a smaller file.')
            return
        }

        try {
            //const data = await mockUploadPhoto(selectedPhoto);
            const data = await uploadPhoto(selectedPhoto);

            if (data) {
                setPhotoData((prevData) => [
                    ...prevData,
                    { submitPhotoUrl: data.submitPhotoUrl, thumbnailUrl: data.thumbnailUrl },
                ]);

                setError(null)
            }
        } catch (err) {
            // Handle any errors that occur during the upload
            setError(`Error uploading photo: ${err.message}`);
        }
    };

    const uploadPhoto = async (photo) => {
        try {
            const formData = new FormData();
            formData.append('photo', photo);
            formData.append('brandId', brandId)

            const res = await fetch(`/bv-uploadphoto?siteId=${siteId}`, {
                method: 'POST',
                body: formData
            })

            if (!res.ok) {
                setError('Failed to upload photo');
                throw new Error(`Failed to upload photo: ${res.statusText}`)
            }

            const data = await res.json()
            setError(null)
            return data;
        } catch (err) {
            setError(err.message)
        }
    }

    useEffect(() => {
        // This effect runs whenever photoData changes

        for (let i = 1; i <= maxPhotos; i++) {
            const fieldId = `photourl_${i}`;
            form.setValue(reviewFormData?.[fieldId]?.Id, null);
        }

        // Update reviewFormData with the photo URLs
        photoData.forEach((photo, index) => {
            const fieldId = `photourl_${index + 1}`;
            form.setValue(reviewFormData?.[fieldId]?.Id, photo.submitPhotoUrl);
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = null
        }

    }, [photoData]); // Dependency array includes photoData

    const handleRemoveImage = (index) => {
        setPhotoData((prevData) => prevData.filter((_, i) => i !== index));
    };

   return (
        <div>
            <Flex wrap="wrap" maxWidth="100%" gap={3}>
                {photoData.length < maxPhotos && (
                    <span>
                        <input
                            id="image-upload"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleFileSelectAndUpload}
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                        />
                        <Button
                            as="label"
                            htmlFor="image-upload"
                            width="48px"
                            height="48px"
                            border="1px solid webTheme.backgroundNotification"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            cursor="pointer"
                            m={0}
                            backgroundColor="webTheme.backgroundNotification"
                            role="button"
                            tabIndex={0}
                            aria-label="Upload image"
                            _hover={{ backgroundColor: "webTheme.backgroundNotification" }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    if(e) e.preventDefault();
                                    fileInputRef.current?.click();
                                }
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5V19M5 12H19" stroke="black" strokeLinecap="square" />
                            </svg>
                        </Button>
                    </span>
                )}

                {photoData.map((photo, index) => (
                    <Box key={index} position="relative" display="inline-block">
                        <Image
                            data-private
                            src={photo.thumbnailUrl}
                            alt={`Uploaded image ${index + 1}`}
                            boxSize="80px"
                            objectFit="cover"
                            borderRadius="md"
                        />
                        <Button
                            size="xs"
                            position="absolute"
                            top="0"
                            right="0"
                            width="24px"
                            height="24px"
                            onClick={() => handleRemoveImage(index)}
                            backgroundColor="white"
                            borderRadius="full"
                            _hover={{ backgroundColor: "white" }}
                        >
                            <Icon as={CustomClose} width="16px" height="16px" />
                        </Button>
                    </Box>
                ))}
            </Flex>

            {error && <Text textStyle="bodyXsRegular" color="webTypo.textError" mt={2}>{error}</Text>}
        </div>
    );
};

export default ImageUploader
